import { useState } from "react";
import { saveGhgReference } from "./referencesApi";
import { referenceDraftError } from "./referenceHelpers";
import "./EmissionFactorBreakdown.css";

let formIdCounter = 0;

/**
 * Creates a reference, or edits `reference`, inline. The saved row is handed
 * to `onSaved`, so a caller can pick a reference it has just created.
 *
 * @param {object} props
 * @param {import("./referencesApi").GhgReference|null} [props.reference] the one being edited; none creates
 * @param {(saved: import("./referencesApi").GhgReference) => void} props.onSaved
 * @param {() => void} props.onCancel
 */
export default function ReferenceForm({ reference, onSaved, onCancel }) {
  const [formId] = useState(() => ++formIdCounter);
  const [draft, setDraft] = useState(() => ({
    title: reference?.title || "",
    url: reference?.url || "",
    note: reference?.note || "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const change = (key) => (e) => {
    const { value } = e.target;
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const problem = referenceDraftError(draft);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    const res = await saveGhgReference(reference?.id ?? null, {
      title: draft.title.trim(),
      url: draft.url.trim(),
      note: draft.note.trim(),
    });
    setSaving(false);
    if (res?.isSuccess && res.data?.data) onSaved(res.data.data);
    else setError(res?.data?.error || "Couldn't save the reference.");
  };

  const fieldId = (key) => `efb-ref-${formId}-${key}`;

  return (
    <form className="efb-refform" onSubmit={submit}>
      <label className="efb-field efb-field--grow" htmlFor={fieldId("title")}>
        <span>Title</span>
        <input
          id={fieldId("title")}
          type="text"
          maxLength={255}
          value={draft.title}
          placeholder="IPCC AR6 WG1 Chapter 7"
          onChange={change("title")}
        />
      </label>
      <label className="efb-field efb-field--grow" htmlFor={fieldId("url")}>
        <span>Link</span>
        <input
          id={fieldId("url")}
          type="url"
          maxLength={1024}
          value={draft.url}
          placeholder="https://"
          onChange={change("url")}
        />
      </label>
      <label className="efb-field" htmlFor={fieldId("note")}>
        <span>Where in it</span>
        <input
          id={fieldId("note")}
          type="text"
          maxLength={500}
          value={draft.note}
          placeholder="Table 7.15, p. 1017"
          onChange={change("note")}
        />
      </label>
      <div className="efb-refform-actions">
        <button type="submit" className="kgb-btn kgb-btn--primary" disabled={saving}>
          {saving ? "Saving…" : reference ? "Save" : "Add reference"}
        </button>
        <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
      {error ? (
        <p className="efb-refform-error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}
